import React, { useContext } from 'react';
import { connect, DispatchProp, useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { Link } from '@dndbeyond/character-common-components/es';
import { CharacterAvatarPortrait } from '@dndbeyond/character-components/es';
import {
    DecorationInfo,
    DecorationUtils,
    FormatUtils,
    rulesEngineSelectors,
} from '@dndbeyond/character-rules-engine/es';

import { NotificationSystem } from '~/components/NotificationSystem';
import config from '~/config';
import { HeadContext } from '~/state/head';

import { appEnvActions, toastMessageActions } from '../../../Shared/actions';
import LoadingBlocker from '../../../Shared/components/LoadingBlocker';
import { BuilderLinkButton } from '../../../Shared/components/common/LinkButton';
import AppErrorTypeEnum from '../../../Shared/constants/AppErrorTypeEnum';
import { DiceContainer } from '../../../Shared/containers/DiceContainer';
import XpManagerModal from '../../../Shared/containers/legacy/XpManager';
import { appEnvSelectors, appInfoSelectors, toastMessageSelectors } from '../../../Shared/selectors';
import { AppInfoErrorState } from '../../../Shared/stores/typings';
import { builderActions } from '../../actions/builder';
import { BUILDER_MQ_IS_MOBILE_SIZE } from '../../config';
import { RouteDef, ROUTE_DEFINITIONS } from '../../config/navigation';
import { BuilderMethod } from '../../constants/navigation';
import { builderSelectors } from '../../selectors';
import { BuilderAppState } from '../../typings';
import { getUrlWithParams } from '../../utils/navigationUtils';
import BuilderTypeChoicePage from '../BuilderTypeChoicePage';
import ConfirmModalManager from '../ConfirmModalManager';
import NavigationSections from '../NavigationSections';
import SynchronousBlocker from '../SynchronousBlocker';
import TodoNavigation from '../TodoNavigation';
import AvatarManagerModal from '../modals/AvatarManager';
import ClassChooseConfirmModal from '../modals/ClassChooseConfirm';
import HpManagerModal from '../modals/HpManager';
import RaceChooseConfirmModal from '../modals/RaceChooseConfirm';

const BASE_PATHNAME = config.basePathname;

interface Props extends DispatchProp {
    appError: AppInfoErrorState | null;
    toastMessages: any;
    builderMethod: any;
    isCharacterLoading: boolean;
    isCharacterLoaded: boolean;
    isMobile: boolean;
    name: string | null;
    username: string | null;
    redirect: string;
    characterId: number | null;
    diceEnabled: boolean;
    decorationInfo: DecorationInfo;
    canEdit: boolean;
    isReadonly: boolean;
}

const RenderAuthError: React.FC<{ appError: Props['appError'] }> | null = ({ appError }) => {
    if (appError === null) {
        return null;
    }

    let contentNode: React.ReactNode;
    switch (appError.type) {
        case AppErrorTypeEnum.AUTH_MISSING:
            let signInUrl: string = `/sign-in?returnUrl=${encodeURIComponent(window.location.pathname)}`;
            contentNode = (
                <React.Fragment>
                    <p>
                        You are no longer signed into D&D Beyond. Go to the <Link url={signInUrl}>Sign In page</Link> to
                        continue the adventure!
                    </p>
                </React.Fragment>
            );
            break;

        case AppErrorTypeEnum.API_FAIL:
            contentNode = (
                <React.Fragment>
                    <p>
                        Whoops! We rolled a 1 on our API check. We're heading into town to visit the blacksmith for
                        repairs.
                    </p>
                    <p>Try again after a Short Rest.</p>
                </React.Fragment>
            );
            break;

        case AppErrorTypeEnum.API_DATA_FAIL:
            contentNode = (
                <React.Fragment>
                    <p>
                        Whoops! We rolled a 1 on our API Data check. We're heading into town to visit the blacksmith for
                        repairs.
                    </p>
                    <p>Try again after a Short Rest.</p>
                </React.Fragment>
            );
            break;

        case AppErrorTypeEnum.AUTH_FAIL:
            contentNode = (
                <React.Fragment>
                    <p>
                        Whoops! We rolled a 1 on our Authentication check. We're heading into town to visit the
                        blacksmith for repairs.
                    </p>
                    <p>Try again after a Short Rest.</p>
                </React.Fragment>
            );
            break;

        case AppErrorTypeEnum.ACCESS_DENIED:
            contentNode = (
                <React.Fragment>
                    <p>
                        As you try to enter the 403rd level of the endless dungeon, the ancient dragon Rylzrayrth rises
                        up to block your path. The dragon has forbidden you from traveling any further.
                    </p>
                    <p>In other words, you don’t have access to complete the action.</p>
                </React.Fragment>
            );
            break;

        case AppErrorTypeEnum.GENERIC:
        default:
            contentNode = (
                <React.Fragment>
                    <p>
                        Whoops! We rolled a 1 on our check. We're heading into town to visit the blacksmith for repairs.
                    </p>
                    <p>Try again after a Short Rest.</p>
                </React.Fragment>
            );
            break;
    }

    return (
        <div
            className={[
                'ct-character-sheet__failed',
                `ct-character-sheet__failed--${FormatUtils.slugify(appError.type)}`,
            ].join(' ')}
        >
            <div className="ct-character-sheet__failed-content">
                {contentNode}
                {appError.errorId && (
                    <React.Fragment>
                        <div className="ct-character-sheet__failed-code">
                            <div className="ct-character-sheet__failed-code-label">Error Code</div>
                            <div className="ct-character-sheet__failed-code-value">{appError.errorId}</div>
                        </div>
                        <div className="ct-character-sheet__failed-code">
                            <div className="ct-character-sheet__failed-code-label">Version</div>
                            <div className="ct-character-sheet__failed-code-value">{config.version}</div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

const checkForMessages = (toastMessages, dispatch, notificationSystem): void => {
    Object.keys(toastMessages).forEach((messageId) => {
        if (!notificationSystem) {
            return;
        }

        const message = toastMessages[messageId];
        const { autoDismiss, dismissible, position } = message.meta;

        if (!notificationSystem.current) {
            return;
        }

        notificationSystem.current.addNotification({
            title: message.payload.title,
            message: message.payload.message,
            level: message.meta.level,
            autoDismiss,
            dismissible,
            position,
            uid: messageId,
            onRemove: () => dispatch(toastMessageActions.removeToast(messageId)),
        });
    });
};
const CharacterBuilder: React.FC<Props> | null = ({
    appError,
    toastMessages,
    builderMethod,
    isCharacterLoading,
    isCharacterLoaded,
    isMobile,
    name,
    username,
    redirect,
    characterId,
    diceEnabled,
    decorationInfo,
    canEdit,
    isReadonly,
}) => {
    let mobileMql: MediaQueryList | null = null;
    // useImperativeHandle is hard to type when we us our own context this will just disapear
    const notificationSystem = React.createRef<any>();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { setTitle } = useContext(HeadContext);

    React.useEffect(() => {
        if (characterId !== null) {
            dispatch(builderActions.characterLoadRequest());
        }
        mobileMql = window.matchMedia(BUILDER_MQ_IS_MOBILE_SIZE);
        dispatch(appEnvActions.mobileSet(mobileMql.matches));
        mobileMql.onchange = (e) => {
            dispatch(appEnvActions.mobileSet(e.matches));
        };
    }, [characterId]);

    React.useEffect(() => {
        if (isCharacterLoaded) {
            dispatch(appEnvActions.dataSet({ isReadonly: !canEdit }));
            if (!canEdit) {
                navigate(getUrlWithParams(`${BASE_PATHNAME}/${characterId}`));
            }
        }
    }, [isCharacterLoaded, canEdit, characterId, navigate, dispatch]);

    React.useEffect(() => {
        checkForMessages(toastMessages, dispatch, notificationSystem);
    });

    React.useEffect(() => {
        setTitle('Character Builder');
    }, [setTitle]);

    if (appError !== null) {
        return <RenderAuthError appError={appError} />;
    }

    let clsNames: Array<string> = ['character-builder'];
    if (builderMethod) {
        clsNames.push(`character-builder-${FormatUtils.slugify(builderMethod)}`);
    }

    // useEffect above handles redirect but this blocks UI from showing
    if (isReadonly && isCharacterLoaded) {
        return null;
    }

    if (!isCharacterLoading && builderMethod === null) {
        let anonNode: React.ReactNode;
        if (!username) {
            anonNode = (
                <div className="character-builder-anon">
                    <div className="character-builder-anon-heading">Start Your Adventure</div>
                    <div className="character-builder-anon-content">
                        <p>
                            You need to be signed in to a D&amp;D Beyond account in order to save characters. You can
                            use your character sheet anywhere, on any device.
                        </p>
                    </div>
                    <div className="character-builder-anon-actions">
                        <BuilderLinkButton size="oversized" url={redirect}>
                            Sign in or Create Account
                        </BuilderLinkButton>
                    </div>
                </div>
            );
        }

        return (
            <Routes>
                <Route
                    path="/"
                    element={
                        <div className="character-builder">
                            <SynchronousBlocker />
                            <div className="character-builder-inner">
                                {anonNode}
                                {<BuilderTypeChoicePage isEnabled={!!username} />}
                            </div>
                        </div>
                    }
                ></Route>
            </Routes>
        );
    }

    if (diceEnabled) {
        clsNames.push('character-builder--dice-enabled');
    }

    return (
        <Routes>
            <Route
                path="/*"
                element={
                    <div className={clsNames.join(' ')}>
                        {(characterId !== null || isCharacterLoading) && (
                            <LoadingBlocker isFinished={isCharacterLoaded} />
                        )}
                        {!isCharacterLoading && (
                            <React.Fragment>
                                <SynchronousBlocker />
                                <div className="character-builder-inner">
                                    {builderMethod === BuilderMethod.STEP_BY_STEP && (
                                        <React.Fragment>
                                            {!isMobile && (
                                                <div className="character-builder-page-header">
                                                    <h1 className="character-builder-page-header-heading">
                                                        Character Builder
                                                    </h1>
                                                    {name && (
                                                        <div className="character-builder-page-header-summary">
                                                            <CharacterAvatarPortrait
                                                                className={'character-builder-page-header-avatar'}
                                                                avatarUrl={
                                                                    DecorationUtils.getAvatarInfo(decorationInfo)
                                                                        .avatarUrl
                                                                }
                                                            />
                                                            <div className="character-builder-page-header-name">
                                                                {name}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <ConfirmModalManager />
                                            <NavigationSections pathname={pathname} characterId={characterId} />
                                            <TodoNavigation pathname={pathname} characterId={characterId} />
                                            <RaceChooseConfirmModal />
                                            <ClassChooseConfirmModal />
                                            <XpManagerModal />
                                            <HpManagerModal />
                                            <AvatarManagerModal />
                                        </React.Fragment>
                                    )}
                                    <Routes>
                                        {Object.values(ROUTE_DEFINITIONS).map(({ key, path, Component }: RouteDef) => {
                                            return (
                                                <Route
                                                    key={key}
                                                    path={path
                                                        .replace(`${BASE_PATHNAME}/builder/`, '')
                                                        .replace(`${BASE_PATHNAME}/:characterId/builder/`, '')}
                                                    element={<Component />}
                                                />
                                            );
                                        })}
                                        <Route path="/" element={<BuilderTypeChoicePage />}></Route>
                                    </Routes>
                                </div>
                            </React.Fragment>
                        )}
                        <NotificationSystem ref={notificationSystem} />
                        {diceEnabled && !isReadonly ? <DiceContainer canShow={isCharacterLoaded} /> : null}
                    </div>
                }
            ></Route>
        </Routes>
    );
};

function mapStateToProps(state: BuilderAppState) {
    return {
        toastMessages: toastMessageSelectors.getToastMessages(state),
        builderMethod: builderSelectors.getBuilderMethod(state),
        isCharacterLoading: builderSelectors.getIsCharacterLoading(state),
        isCharacterLoaded: builderSelectors.getIsCharacterLoaded(state),
        isMobile: appEnvSelectors.getIsMobile(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        name: rulesEngineSelectors.getName(state),
        decorationInfo: rulesEngineSelectors.getDecorationInfo(state),
        canEdit: rulesEngineSelectors.getCanEdit(state),
        username: appEnvSelectors.getUsername(state),
        redirect: appEnvSelectors.getRedirect(state),
        appError: appInfoSelectors.getError(state),
        characterId: appEnvSelectors.getCharacterId(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
    };
}

export default connect(mapStateToProps)(CharacterBuilder);
